import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import SignaturePad from '../../engagement/SignaturePad';
import type { ContractorAgreement, ContractorAgreementFields, ContractorApplicant } from '../../../types/contractorOnboard';
import type { CrewJob } from '../../../types/crewJobs';
import {
  assembleContractorAgreementSections,
  contractorAgreementFullName,
  defaultContractorAgreementFields,
  formatContractorPay,
  normalizeContractorScopeTools,
  overlaySelectedJobOnAgreementFields,
} from '../../../utils/contractorAgreement';
import {
  createContractorAgreementDraft,
  saveContractorAgreementDraft,
  sendContractorAgreement,
} from '../../../utils/contractorOnboardFirestore';
import { crewOnboardPublicUrl } from '../../../utils/contractorOnboard';
import { payBandFromJob } from '../../../utils/crewJobsPay';
import { LCTF_OFFER_COMPANY, LCTF_OFFER_LEGAL } from '../../../utils/jobOfferLetter';
import type { ContractorOnboardViewerOpen } from '../../../utils/contractorOnboardViewer';
import ContractorAgreementPdfButtons from './ContractorAgreementPdfButtons';

type Props = {
  applicant: ContractorApplicant;
  agreement: ContractorAgreement | null;
  job?: CrewJob | null;
  onOpenPacketDoc?: (doc: ContractorOnboardViewerOpen) => void;
};

const ContractorAgreementPane: React.FC<Props> = ({ applicant, agreement, job, onOpenPacketDoc }) => {
  const [fields, setFields] = useState<ContractorAgreementFields>(() => defaultFields(applicant, job));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ceoDraft, setCeoDraft] = useState<{ dataUrl: string; signedBy: string } | null>(null);
  const [signOpen, setSignOpen] = useState(false);
  const draftIdRef = useRef<string | null>(agreement?.id || null);
  const skipAutosaveRef = useRef(true);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');

  useEffect(() => {
    draftIdRef.current = agreement?.id || null;
    skipAutosaveRef.current = true;
    const base = agreement?.fields
      ? { ...agreement.fields, scope: normalizeContractorScopeTools(agreement.fields.scope || '') }
      : defaultFields(applicant, job);
    setFields(overlaySelectedJobOnAgreementFields(base, selectedJobOverlay(job)));
    setCeoDraft(null);
    setSignOpen(false);
  }, [applicant.id, agreement?.id, job?.id]);

  useEffect(() => {
    if (skipAutosaveRef.current) {
      skipAutosaveRef.current = false;
      return;
    }
    if (agreement && agreement.status !== 'draft') return;
    const handle = window.setTimeout(() => {
      void persistDraft();
    }, 600);
    return () => window.clearTimeout(handle);
  }, [fields]);

  const persistDraft = async (withCeo = false) => {
    setSaveState('saving');
    try {
      const ceo = withCeo && ceoDraft
        ? {
          dataUrl: ceoDraft.dataUrl,
          signedBy: ceoDraft.signedBy,
          signedAt: new Date(),
          title: fields.ceoTitle,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 240) : undefined,
          source: 'crm',
        }
        : agreement?.ceoSignature || null;
      if (!draftIdRef.current) {
        draftIdRef.current = await createContractorAgreementDraft({ applicantId: applicant.id, fields });
      }
      await saveContractorAgreementDraft(draftIdRef.current, {
        fields,
        ...(withCeo ? { ceoSignature: ceo } : {}),
      });
      setSaveState('saved');
    } catch (e) {
      setError((e as Error).message);
      setSaveState('idle');
    }
  };

  const openCeoSign = async () => {
    setError(null);
    if (!fields.payAmount) {
      setError('Set a pay rate before signing');
      return;
    }
    setBusy(true);
    try {
      await persistDraft(false);
      setSignOpen(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const signAndSend = async () => {
    if (!ceoDraft) {
      setError('Sign and type your name before sending');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await persistDraft(true);
      if (!draftIdRef.current) throw new Error('Could not save the agreement');
      await sendContractorAgreement(draftIdRef.current, applicant.id);
      setSignOpen(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (agreement && agreement.status !== 'draft') {
    return (
      <div className="crew-ica-sent">
        <strong>
          {agreement.status === 'signed'
            ? `Signed — ${formatContractorPay(agreement.fields)}`
            : `Sent — ${formatContractorPay(agreement.fields)}`}
        </strong>
        <span className="people-hiring-muted">
          {agreement.status === 'signed'
            ? 'Contractor signed. They are in the 1099 pool.'
            : 'Waiting on contractor signature.'}
        </span>
        {applicant.publicSlug && (
          <a className="pm-btn pm-btn-ghost" href={crewOnboardPublicUrl(applicant.publicSlug)} target="_blank" rel="noreferrer">
            Open public page
          </a>
        )}
        {agreement.status === 'signed' && (
          <ContractorAgreementPdfButtons
            applicant={applicant}
            agreement={agreement}
            onOpenPacketDoc={onOpenPacketDoc}
          />
        )}
      </div>
    );
  }

  // Avoid flashing the draft editor while the sent/signed agreement is still loading.
  if (!agreement && (applicant.status === 'agreement_sent' || applicant.status === 'pooled' || applicant.agreementId)) {
    return (
      <div className="crew-ica-sent">
        <span className="people-hiring-muted">Loading agreement…</span>
      </div>
    );
  }

  const name = contractorAgreementFullName(fields) || 'Contractor';
  const pay = formatContractorPay(fields);
  const sections = assembleContractorAgreementSections(fields);

  return (
    <div className="crew-ica">
      <div className="crew-ica-head">
        <strong>1099 agreement</strong>
        <span className="people-hiring-muted">
          {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved' : 'Draft'}
        </span>
      </div>

      <div className="crew-ica-grid">
        <label className="crew-ica-field">
          <span>First</span>
          <input value={fields.contractorFirstName} onChange={e => setFields(f => ({ ...f, contractorFirstName: e.target.value }))} />
        </label>
        <label className="crew-ica-field">
          <span>Last</span>
          <input value={fields.contractorLastName} onChange={e => setFields(f => ({ ...f, contractorLastName: e.target.value }))} />
        </label>
        <label className="crew-ica-field">
          <span>Pay</span>
          <input
            type="number"
            min={0}
            value={fields.payAmount || ''}
            onChange={e => setFields(f => ({ ...f, payAmount: Number(e.target.value) || 0 }))}
          />
        </label>
        <label className="crew-ica-field">
          <span>Unit</span>
          <select
            value={fields.payUnit}
            onChange={e => setFields(f => ({ ...f, payUnit: e.target.value === 'day' ? 'day' : 'hour' }))}
          >
            <option value="day">Day</option>
            <option value="hour">Hour</option>
          </select>
        </label>
        <label className="crew-ica-field crew-ica-field--wide">
          <span>Work / raise</span>
          <input value={fields.jobTitle} onChange={e => setFields(f => ({ ...f, jobTitle: e.target.value }))} />
        </label>
        <label className="crew-ica-field crew-ica-field--wide">
          <span>Scope</span>
          <textarea rows={2} value={fields.scope} onChange={e => setFields(f => ({ ...f, scope: e.target.value }))} />
        </label>
      </div>

      <p className="crew-ica-hint people-hiring-muted">
        Preparing an independent contractor agreement for <strong>{name}</strong> at <strong>{pay}</strong>.
        Review terms and sign before sending.
      </p>

      {error && !signOpen && <p className="people-hiring-resume-error">{error}</p>}

      <div className="people-hiring-resume-actions">
        <button
          type="button"
          className="ops-new-btn"
          disabled={busy || !fields.payAmount}
          onClick={() => void openCeoSign()}
        >
          {busy ? 'Opening…' : 'Send Offer Letter'}
        </button>
      </div>

      {signOpen && (
        <div
          className="people-hiring-modal-backdrop people-hiring-offer-preview-backdrop"
          onClick={() => !busy && setSignOpen(false)}
        >
          <div
            className="people-hiring-modal people-hiring-offer-preview-modal crew-ica-sign-modal"
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="crew-ica-sign-title"
          >
            <div className="people-hiring-modal-header">
              <div>
                <h3 id="crew-ica-sign-title">Send Offer Letter</h3>
                <p className="people-hiring-muted" style={{ margin: '4px 0 0' }}>
                  Independent Contractor Agreement — {LCTF_OFFER_COMPANY}
                </p>
              </div>
              <button
                type="button"
                className="pm-btn pm-btn-ghost"
                aria-label="Close"
                disabled={busy}
                onClick={() => setSignOpen(false)}
              >
                <X size={16} />
              </button>
            </div>

            <div className="people-hiring-modal-body crew-ica-sign-body">
              <section className="crew-ica-summary">
                <div>
                  <span className="crew-ica-summary-label">Contractor</span>
                  <strong>{name}</strong>
                  <span className="people-hiring-muted">{fields.contractorEmail}</span>
                </div>
                <div>
                  <span className="crew-ica-summary-label">Engagement</span>
                  <strong>{fields.jobTitle || '—'}</strong>
                  <span className="people-hiring-muted">{fields.location || 'Location TBD'}</span>
                </div>
                <div>
                  <span className="crew-ica-summary-label">Pay</span>
                  <strong>{pay}</strong>
                  <span className="people-hiring-muted">1099 · not W-2 wages</span>
                </div>
              </section>

              <section className="crew-ica-walkthrough">
                <h4>What you are signing</h4>
                <ul>
                  <li>
                    Authorizing <strong>{LCTF_OFFER_LEGAL}</strong> to engage <strong>{name}</strong> as a
                    {' '}<strong>1099 independent contractor</strong> (not an employee).
                  </li>
                  <li>
                    Committing the company to pay <strong>{pay}</strong>
                    {fields.payUnit === 'day'
                      ? ' (day rate based on a typical 10-hour day; workdays may run 6–12 hours)'
                      : ''}
                    {' '}for accepted raise / on-site work, plus necessary travel expenses and travel time.
                  </li>
                  <li>
                    Confirming tools may be contractor-supplied, otherwise LCTF provides them; and that the
                    company will <strong>not</strong> withhold payroll taxes or provide W-2 benefits for this
                    engagement — the contractor handles their own taxes.
                  </li>
                  <li>
                    After you sign, the agreement is emailed to {name} for their signature. When they sign, they enter
                    the reusable 1099 pool.
                  </li>
                </ul>
              </section>

              <article className="crew-ica-doc">
                <header>
                  <p className="crew-ica-doc-kicker">{LCTF_OFFER_COMPANY}</p>
                  <h4>Independent Contractor Agreement</h4>
                  <p className="people-hiring-muted">{LCTF_OFFER_LEGAL}</p>
                </header>
                {sections.filter(s => s.id !== 'letterhead').map(section => (
                  <section key={section.id}>
                    <h5>{section.heading}</h5>
                    <p>{section.body}</p>
                  </section>
                ))}
              </article>

              <section className="crew-ica-sign-pad">
                <h4>Your signature (CEO)</h4>
                <p className="people-hiring-muted">
                  Sign as {fields.ceoName || 'CEO'}, {fields.ceoTitle || 'CEO'}. Typing your name confirms you reviewed
                  the terms above.
                </p>
                <SignaturePad
                  initialName={fields.ceoName}
                  onChange={setCeoDraft}
                  onSign={setCeoDraft}
                  hideSubmitButton
                />
              </section>

              {error && <p className="people-hiring-resume-error">{error}</p>}
            </div>

            <div className="crew-ica-sign-footer">
              <button type="button" className="pm-btn pm-btn-ghost" disabled={busy} onClick={() => setSignOpen(false)}>
                Back to edit
              </button>
              <button
                type="button"
                className="ops-new-btn"
                disabled={busy || !ceoDraft}
                onClick={() => void signAndSend()}
              >
                {busy ? 'Sending…' : 'Sign & send offer letter'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function selectedJobOverlay(job?: CrewJob | null) {
  if (!job) return null;
  const band = payBandFromJob(job);
  return {
    title: job.title,
    location: job.location,
    payAmount: band?.min || band?.max || null,
    payUnit: band?.unit || null,
  };
}

function defaultFields(applicant: ContractorApplicant, job?: CrewJob | null): ContractorAgreementFields {
  const overlay = selectedJobOverlay(job);
  return overlaySelectedJobOnAgreementFields(
    defaultContractorAgreementFields({
      firstName: applicant.firstName,
      lastName: applicant.lastName,
      email: applicant.email,
      jobTitle: overlay?.title || applicant.lastJobTitle,
      location: overlay?.location,
      payAmount: overlay?.payAmount,
      payUnit: overlay?.payUnit || 'day',
    }),
    overlay,
  );
}

export default ContractorAgreementPane;
